import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Crown, Shield, Star, Undo, Zap, Globe, Check, Gift, Calendar, CreditCard, X } from 'lucide-react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { paymentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { BottomNav } from '../components/BottomNav';

const TRIAL_DAYS = 14;

const TIERS = {
  gold: {
    name: 'Gold',
    price: 19.99,
    icon: Crown,
    color: 'gold',
    gradient: 'from-yellow-600 to-yellow-400',
    features: [
      'Unlimited Swipes',
      '5 Super Likes per day',
      'See who likes you',
      'Rewind last swipe',
      'Profile Boost monthly'
    ]
  },
  platinum: {
    name: 'Platinum',
    price: 29.99,
    icon: Sparkles,
    color: 'platinum',
    gradient: 'from-purple-600 to-pink-400',
    features: [
      'Everything in Gold',
      'Unlimited Super Likes',
      'Priority matching',
      'Advanced filters',
      'Read receipts',
      'Weekly Profile Boost'
    ]
  }
};

export default function PremiumPage() {
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState('gold');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ stripe: { available: true }, paypal: { available: true } });

  useEffect(() => {
    // Check for Stripe success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
      return;
    }

    // Check for PayPal success (NVP API returns token and PayerID)
    const token = searchParams.get('token');
    const PayerID = searchParams.get('PayerID');
    if (token && PayerID) {
      executePayPalPayment(token, PayerID);
      return;
    }

    // Check for PayPal return from approval
    if (location.pathname.includes('paypal-success')) {
      const tier = searchParams.get('tier');
      if (tier) setSelectedTier(tier);
    }

    // Load subscription info if premium
    if (user?.is_premium) {
      loadSubscriptionInfo();
    }

    // Load available payment methods
    loadPaymentMethods();
  }, [searchParams, user, location]);

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentAPI.getPaymentMethods();
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const response = await paymentAPI.getSubscription();
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to load subscription info:', error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await paymentAPI.status(sessionId);
      if (response.data.status === 'active' || response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        await checkAuth();
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    }
  };

  const executePayPalPayment = async (token, PayerID) => {
    setLoading(true);
    try {
      const response = await paymentAPI.paypalExecute(token, PayerID);
      if (response.data.success) {
        setPaymentStatus('success');
        await checkAuth();
      }
    } catch (error) {
      console.error('PayPal execution failed:', error);
      alert(error.response?.data?.detail || 'PayPal payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    setLoading(true);
    try {
      if (selectedPaymentMethod === 'paypal') {
        // PayPal checkout
        const response = await paymentAPI.paypalCreateSubscription({
          tier,
          origin_url: window.location.origin
        });
        window.location.href = response.data.approval_url;
      } else {
        // Stripe checkout
        const response = await paymentAPI.checkout({
          tier,
          origin_url: window.location.origin
        });
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      alert(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await paymentAPI.cancelSubscription();
      setShowCancelModal(false);
      await loadSubscriptionInfo();
      alert('Your subscription will be cancelled at the end of the billing period.');
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  // Success screen after payment
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center p-6" data-testid="premium-success">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-24 h-24 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6 animate-pulse-gold">
            <Gift className="w-12 h-12 text-gold" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-2">Welcome to Premium!</h1>
          <p className="text-gray-400 mb-4 font-body">
            Your {TRIAL_DAYS}-day free trial has started
          </p>
          <div className="bg-dark-paper border border-gold/30 rounded-2xl p-4 mb-8">
            <div className="flex items-center gap-3 text-gold">
              <Calendar className="w-5 h-5" />
              <span className="font-body">You won't be charged until your trial ends</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/discover')}
            className="btn-gold px-8 py-3 rounded-full font-body font-semibold"
          >
            Start Discovering
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-32" data-testid="premium-page">
      {/* Hero */}
      <div className="relative px-6 pt-12 pb-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/10 to-transparent" />
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="relative text-center"
        >
          <Crown className="w-16 h-16 text-gold mx-auto mb-4" />
          <h1 className="font-heading text-4xl font-bold text-gold-gradient mb-2">
            Go Premium
          </h1>
          <p className="text-gray-400 font-body max-w-xs mx-auto">
            Start your {TRIAL_DAYS}-day free trial today
          </p>
        </motion.div>
      </div>

      {/* Trial Banner */}
      <div className="mx-6 mb-6 p-4 bg-gradient-to-r from-gold/20 to-gold/10 border border-gold/30 rounded-2xl">
        <div className="flex items-center gap-3">
          <Gift className="w-8 h-8 text-gold flex-shrink-0" />
          <div>
            <p className="text-gold font-semibold font-body">{TRIAL_DAYS}-Day Free Trial</p>
            <p className="text-sm text-gray-300 font-body">
              Try all premium features free. Cancel anytime before trial ends.
            </p>
          </div>
        </div>
      </div>

      {/* Current Subscription Status */}
      {user?.is_premium && subscriptionInfo && (
        <div className="mx-6 mb-6 p-4 bg-dark-paper border border-gold/30 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-gold" />
              <div>
                <p className="text-gold font-semibold">
                  {user.premium_tier?.charAt(0).toUpperCase() + user.premium_tier?.slice(1)} Member
                </p>
                <p className="text-sm text-gray-400 font-body">
                  {subscriptionInfo.trial_end && new Date(subscriptionInfo.trial_end) > new Date() 
                    ? `Trial ends ${new Date(subscriptionInfo.trial_end).toLocaleDateString()}`
                    : subscriptionInfo.current_period_end 
                      ? `Renews ${new Date(subscriptionInfo.current_period_end).toLocaleDateString()}`
                      : 'Active subscription'
                  }
                </p>
              </div>
            </div>
            {!subscriptionInfo.cancel_at_period_end && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-sm text-gray-400 hover:text-red-400 font-body"
              >
                Cancel
              </button>
            )}
          </div>
          {subscriptionInfo.cancel_at_period_end && (
            <p className="text-sm text-yellow-400 font-body">
              Your subscription will end on {new Date(subscriptionInfo.current_period_end).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Tier Selection */}
      <div className="px-6 space-y-4">
        {Object.entries(TIERS).map(([key, tier]) => {
          const Icon = tier.icon;
          const isSelected = selectedTier === key;
          const isCurrentTier = user?.premium_tier === key;
          
          return (
            <motion.button
              key={key}
              onClick={() => setSelectedTier(key)}
              whileTap={{ scale: 0.98 }}
              className={`w-full p-5 rounded-2xl border text-left transition-all duration-300 relative ${
                isSelected
                  ? 'bg-dark-elevated border-gold shadow-[0_0_30px_rgba(212,175,55,0.2)]'
                  : 'bg-dark-paper border-white/10 hover:border-white/20'
              }`}
              data-testid={`tier-${key}`}
            >
              {/* Popular badge for Platinum */}
              {key === 'platinum' && (
                <div className="absolute -top-3 right-4 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                  <span className="text-xs font-bold text-white">MOST POPULAR</span>
                </div>
              )}

              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${tier.gradient}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white">{tier.name}</h3>
                    {isCurrentTier && (
                      <span className="text-xs text-gold">Current Plan</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl text-white">${tier.price}</p>
                  <p className="text-xs text-gray-400">/month after trial</p>
                </div>
              </div>

              <ul className="space-y-2 mb-4">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-gold flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Selection indicator */}
              <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ml-auto ${
                isSelected ? 'border-gold bg-gold' : 'border-gray-500'
              }`}>
                {isSelected && <Check className="w-4 h-4 text-black" />}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Payment Method Selection */}
      <div className="px-6 mt-6">
        <h3 className="text-white font-body font-semibold mb-3">Select Payment Method</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* Stripe Option */}
          <button
            onClick={() => setSelectedPaymentMethod('stripe')}
            className={`p-4 rounded-xl border text-center transition-all ${
              selectedPaymentMethod === 'stripe'
                ? 'bg-dark-elevated border-gold'
                : 'bg-dark-paper border-white/10 hover:border-white/20'
            }`}
            data-testid="payment-stripe"
          >
            <CreditCard className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === 'stripe' ? 'text-gold' : 'text-gray-400'}`} />
            <p className={`font-body text-sm font-medium ${selectedPaymentMethod === 'stripe' ? 'text-white' : 'text-gray-400'}`}>
              Credit Card
            </p>
            <p className="text-xs text-gray-500 mt-1">Visa, Mastercard, Amex</p>
          </button>

          {/* PayPal Option */}
          <button
            onClick={() => setSelectedPaymentMethod('paypal')}
            className={`p-4 rounded-xl border text-center transition-all ${
              selectedPaymentMethod === 'paypal'
                ? 'bg-dark-elevated border-gold'
                : 'bg-dark-paper border-white/10 hover:border-white/20'
            }`}
            data-testid="payment-paypal"
          >
            <svg className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === 'paypal' ? 'text-[#00457C]' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.31c2.097 0 3.77.554 4.97 1.645 1.2 1.09 1.756 2.584 1.655 4.435-.1 1.85-.816 3.363-2.127 4.489-1.31 1.127-3.036 1.69-5.123 1.69H9.082l-.93 5.264a.77.77 0 0 1-.757.644H7.34l-.264 1.094z"/>
              <path d="M18.27 7.97c-.086.54-.224 1.057-.413 1.548-.69 1.8-2.15 3.076-4.232 3.538-.46.1-.94.153-1.434.153H9.923l-.933 5.262a.77.77 0 0 1-.757.644H5.89l1.17-6.55c0-.01.003-.02.006-.028l.02-.1h2.238a.77.77 0 0 0 .757-.644l.68-3.82h2.06c1.78 0 3.193-.453 4.192-1.34.47-.417.847-.9 1.13-1.44l.126.777z"/>
            </svg>
            <p className={`font-body text-sm font-medium ${selectedPaymentMethod === 'paypal' ? 'text-white' : 'text-gray-400'}`}>
              PayPal
            </p>
            <p className="text-xs text-gray-500 mt-1">Pay with PayPal</p>
          </button>
        </div>
      </div>

      {/* Security Info */}
      <div className="px-6 mt-4">
        <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
          <Shield className="w-4 h-4" />
          <span className="font-body">Secure payment • Cancel anytime</span>
        </div>
      </div>

      {/* Subscribe Button */}
      <div className="fixed bottom-20 left-0 right-0 px-6 py-4 bg-gradient-to-t from-dark via-dark to-transparent">
        <button
          onClick={() => handleSubscribe(selectedTier)}
          disabled={loading || (user?.is_premium && user?.premium_tier === selectedTier)}
          className="w-full btn-gold py-4 rounded-full font-body font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="subscribe-btn"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              Processing...
            </>
          ) : user?.is_premium && user?.premium_tier === selectedTier ? (
            'Current Plan'
          ) : (
            <>
              <Gift className="w-5 h-5" />
              Start {TRIAL_DAYS}-Day Free Trial
            </>
          )}
        </button>
        <p className="text-center text-xs text-gray-500 mt-2 font-body">
          ${TIERS[selectedTier].price}/month after trial • Cancel anytime
        </p>
      </div>

      {/* Features Grid */}
      <div className="px-6 mt-8 mb-8">
        <h2 className="font-heading text-xl text-white mb-4">Premium Benefits</h2>
        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: Zap, title: 'Unlimited Swipes', desc: 'No daily limits' },
            { icon: Star, title: 'Super Likes', desc: 'Stand out more' },
            { icon: Undo, title: 'Rewind', desc: 'Undo mistakes' },
            { icon: Globe, title: 'Global Search', desc: 'Match worldwide' },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="p-4 bg-dark-paper border border-white/5 rounded-xl"
            >
              <Icon className="w-8 h-8 text-gold mb-2" />
              <h3 className="font-body font-semibold text-white text-sm">{title}</h3>
              <p className="text-xs text-gray-400">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-dark-paper rounded-2xl p-6 max-w-sm w-full border border-white/10"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-heading text-xl text-white">Cancel Subscription?</h3>
              <button onClick={() => setShowCancelModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-400 font-body mb-6">
              You'll lose access to all premium features at the end of your billing period. Are you sure?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 border border-white/20 rounded-full text-white font-body hover:bg-white/5"
              >
                Keep Premium
              </button>
              <button
                onClick={handleCancelSubscription}
                className="flex-1 py-3 bg-red-500/20 border border-red-500/30 rounded-full text-red-400 font-body hover:bg-red-500/30"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
